<template>
  <v-card class="no-margins" flat>

    <h5 class="pt-3"><v-icon class="mr-2">$sl-user-protect</v-icon>Skyddad identitet</h5>
    <p>Information tillhörande personer med skyddad identitet kan enbart visas av BYN:s kanslister.</p>
    <v-btn color="secondary" :disabled="!canPersonProtectUpdate" @click="protectAccount(!selectedPerson.isProtected)">
      <v-icon>{{ selectedPerson.isProtected ? '$sl-unlocked' : '$sl-locked' }}</v-icon>
      {{ selectedPerson.isProtected ? 'Ta bort skydd' : 'Skydda identiteten' }}
    </v-btn>


    <h5 class="pt-10"><v-icon class="mr-2">$sl-password-lock-1</v-icon>Inloggning med e-postadress och lösenord</h5>
    <div>
      <div>
        Personer som saknar svenskt personnummer kan <a>ansöka om att kunna logga in med e-postadress och lösenord</a>.
      </div>
      <p>
        <strong>Status:</strong> Kontot
        <strong>
          <u>{{ selectedPerson.loginByEmail ? 'använder' : 'använder inte' }}</u>
        </strong>
        lösenordsinloggning.
      </p>
    </div>
    <div v-if="selectedPerson.loginByEmail">
      <v-btn v-if="selectedPerson.loginByEmail" color="secondary" @click="changePassword()" class="mr-4" :disabled="!canUpdate">
        <v-icon>$sl-password-type</v-icon>
        ÄNDRA LÖSENORD
      </v-btn>
    </div>
    <v-btn
      :disabled="
        !selectedPerson.loginByEmail && (!selectedPerson.personalNumber || selectedPerson.personalNumberType !== 2) || !canUpdate
      "
      color="secondary"
      @click="changePasswordStatus(!selectedPerson.loginByEmail)"
      class="mt-4 mb-4"
    >
      <v-icon>{{ selectedPerson.loginByEmail ? '$sl-password-denied' : '$sl-password-approved' }}</v-icon>
      {{ selectedPerson.loginByEmail ? 'INAKTIVERA LÖSENORDSINLOGGNING' : 'AKTIVERA LÖSENORDSINLOGGNING' }}
    </v-btn>
    <p
      v-if="!selectedPerson.loginByEmail && (!selectedPerson.personalNumber || selectedPerson.personalNumberType !== 2)"
    >
      Det går inte att aktivera lösenordsinloggning eftersom det kräver att personen har ett utländskt personnummer
      angivet.
    </p>

    <template v-if="canLoginAs">
      <h5 class="pt-10">Logga in som</h5>
      <p>{{loginAsText}}</p>
      <v-btn color="secondary" @click="loginAsHash()" class="mr-4">
        <v-icon>$sl-password-type</v-icon>
        Logga in som {{personFullName}}
      </v-btn>
    </template>

    <h5 class="pt-10">Spärra konto</h5>
    <p>Spärrade konton går inte att logga in till. Alla spärrningar av konton loggas.</p>
    <v-btn color="secondary" @click="lockAccount(!selectedPerson.isLocked)" :disabled="!canUpdate">
      <v-icon>{{ selectedPerson.isLocked ? '$sl-unlocked' : '$sl-locked' }}</v-icon>
      {{ selectedPerson.isLocked ? 'Lås upp kontot' : 'Spärra kontot' }}
    </v-btn>

    <h5 class="pt-10">Inaktivera konto</h5>
    <p>
      Inaktiva konton kan inte användas vare sig i Registret eller Mina sidor innan de aktiveras igen. Alla
      inaktiveringar av konton loggas.
    </p>

    <v-btn color="secondary" @click="inactivateAccount(!selectedPerson.isActive)" :disabled="!canUpdate">
      <v-icon>{{ selectedPerson.isActive ? '$sl-locked' : '$sl-unlocked' }}</v-icon>
      {{ selectedPerson.isActive ? 'Inaktivera kontot' : 'Aktivera kontot' }}
    </v-btn>

    <h5 class="pt-10">Radera konto</h5>
    <p>Raderade konton går inte att återställa. Alla raderingar av konton loggas.</p>
    <v-btn color="secondary" @click="deleteAccount()" :disabled="!canDelete"> <v-icon>$sl-delete</v-icon>Radera kontot </v-btn>
    <v-dialog v-model="passwordChangeModal" max-width="600px">
      <PersonChangePasswordModal
        ref="passwordChangeModal"
        @close="passwordChangeModal = false"
        :person="selectedPerson"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import _get from 'lodash/get'
import PersonChangePasswordModal from '@/components/person/PersonChangePasswordModal'
import { CanIDo } from '@/components/mixins'

// @group Person
// PersonAdministration component
export default {
  name: 'PersonAdministration',
  mixins: [CanIDo],
  components: {
    PersonChangePasswordModal
  },
  data: () => ({
    passwordChangeModal: false
  }),

  computed: {
    ...mapGetters('app', ['isLoading']),
    ...mapGetters('person', ['selectedPerson']),
    ...mapGetters('currentuser', ['currentUserRole']),

    isAdmin(){
      return this.currentUserRole === 'admin'
    },

    isRegionary(){
      return this.currentUserRole === 'region'
    },

    isRegistry(){
      return this.currentUserRole === 'registry'
    },

    canLoginAs(){
      return this.isRegionary || this.isAdmin || this.isRegistry
    },

    loginAsText(){
      if(this.isAdmin){
        return `Som administratör kan du logga in i Mina Sidor som ${this.personFullName}. Alla inloggningar som en annan person loggas i en speciell log.`
      }
      if(this.isRegionary){
        return `Som regionär kan du logga in i Mina Sidor som ${this.personFullName}. Du kan endast se vad ${this.personFullName} ser, du kan inte ändra något. Alla inloggningar som en annan person loggas i en separat fil.`
      }
      if(this.isRegistry){
        return `Som kanslist kan du logga in i Mina Sidor som ${this.personFullName}. Du kan endast se vad ${this.personFullName} ser, du kan inte ändra något. Alla inloggningar som en annan person loggas i en separat fil.`
      }

      return ''
    },

    personFullName() {
      return this.selectedPerson ? this.selectedPerson.firstName + ` ${this.selectedPerson.lastName}` : '';
    },
  },

asyncComputed: {
    canUpdate(){
      return this.canIDo('person', 'update');
    },
    canDelete(){
      return this.canIDo('person', 'delete');
    },
    canPersonProtectUpdate(){
      return this.canIDo('person-protect', 'update');
    },

  },

  methods: {
    ...mapActions('person', ['updateSelectedPerson', 'setSelectedPerson']),

    /**
     * @vuese
     * Used to update person
     * @arg The argument is an integer representing person  id and updated with protected toggle
     */
    protectAccount(toggle) {
      API.updatePerson(this.selectedPerson.id, {isProtected: toggle})
        .then(result => {
          /**
           * @vuese
           * Used to update local store with isProtected argument
           */
          this.updateSelectedPerson({isProtected: toggle})
          this.$events.fire('showInfo', 'Identiteten är nu ' + (toggle ? 'skyddad.' : 'oskyddad.'))
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
          )
        })
    },
    /**
     * @vuese
     * Used to update person
     * @arg The argument is an integer representing person  id and updated with locked toggle
     */
    lockAccount(toggle) {
      API.updatePerson(this.selectedPerson.id, {isLocked: toggle})
        .then(result => {
          /**
           * @vuese
           * Used to update local store with isLocked argument
           */
          this.updateSelectedPerson({isLocked: toggle})
          this.$events.fire('showInfo', 'Kontot är nu ' + (toggle ? 'låst.' : 'upplåst.'))
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
          )
        })
    },
    /**
     * @vuese
     * Used to update person with inactive account
     * @arg The argument is an integer representing person  id and updated with locked toggle
     */
    inactivateAccount(toggle) {
      API.updatePerson(this.selectedPerson.id, {isActive: toggle})
        .then(result => {
          /**
           * @vuese
           * Used to update local store with isLocked argument
           */
          this.updateSelectedPerson({isActive: toggle})
          this.$events.fire('showInfo', 'Kontot är nu ' + (toggle ? 'aktivt.' : 'inaktivt.'))
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
          )
        })
    },

    async deleteAccount() {
      const res = await this.$dialog.confirm({
        text: 'Radering av person går inte att ångra.',
        title: 'Vill du verkligen radera denna person?',
        width: '550',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Radera', key: true, icon: '$sl-delete', outlined: true}
        ]
      })
      if (res !== true) return

      API.deletePerson(this.selectedPerson.id)
        .then(result => {
          this.$router.push({name: 'PersonList'})
          this.$events.fire('showInfo', 'Person borttagen')
        })
        .catch(error => {
          const errorType = _get(error, 'response.data.name', 'SequelizeError')
          this.$events.fire(
            'showError',
            errorType === 'SequelizeForeignKeyConstraintError' ?
              `Det går inte att radera denna person eftersom den har kopplingar till andra personer, exempelvis lärlingar. Du behöver ta bort kopplingar till lärlingar innan du kan radera personen.` :
              `Ett fel uppstod (${errorType}), om detta händer igen v.v. kontakta BYN:s kansli.`
          )
        })
    },

    changePassword() {
      this.passwordChangeModal = true
    },

    async loginAsHash() {
      const res = await API.loginAsHash(this.selectedPerson.id);

      window.open(res.data.url);
    },

    async changePasswordStatus(toggle) {
      API.updatePerson(this.selectedPerson.id, {loginByEmail: toggle})
        .then(result => {
          this.updateSelectedPerson({loginByEmail: toggle})
          this.$events.fire('showInfo', 'Lösenord inloggining ar nu ' + (toggle ? 'aktivt.' : 'inaktivt.'))
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.'
          )
        })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
